//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { Help, Information, DocumentDownload } from '@carbon/icons-react';
import DocumentationIcon from '../../icons/documentation.svg';
import AboutCard from '../card/aboutCard';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { getInitials } from '../../utils/avatarUtils';
import { stringToColor } from '../../utils/colorUtils';
import { useUserAuth } from '../../UserAuth';
import { useConfig } from '../../Config';

interface SupportMenuProps {
  userName?: string;
}

function SupportMenu({ userName }: SupportMenuProps) {
  const { t } = useTranslation();
  const [aboutOpen, setAboutOpen] = useState(false);
  const navigate = useNavigate();
  const { roles } = useUserAuth();
  const { services } = useConfig();
  const showDiagnosticArchives = services?.diagnosticArchives?.show ?? true;

  return (
    <>
      <DropdownMenu>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  className="rounded p-2 transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1"
                  aria-label={t('Help')}
                  data-testid="support-icon"
                  id="support-icon"
                  type="button"
                >
                  <Help size={20} className="text-white" />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>{t('Help')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <DropdownMenuContent
          className="w-64"
          id="support-dropdown"
          data-testid="support-dropdown"
        >
          {/* Header with Support Info */}
          <div
            className="flex items-center gap-4 border-b border-gray-200 p-6 dark:border-gray-700"
            id="id-support-menu-header"
            data-testid="support-menu-header"
          >
            <Avatar
              className="size-10 shrink-0"
              style={{
                backgroundColor: stringToColor(
                  userName && userName.trim() !== '' ? userName : 'Guest',
                ),
              }}
            >
              <AvatarFallback>{getInitials(userName, 'Guest')}</AvatarFallback>
            </Avatar>
            <div
              className="flex min-w-0 flex-1 flex-col"
              id="id-support-menu-info"
              data-testid="support-menu-info"
            >
              <div
                className="text-xs font-medium text-gray-500 dark:text-gray-400"
                id="id-support-menu-help-label"
                data-testid="support-menu-help-label"
              >
                {t('Help')}
              </div>
              <div
                className="mt-1 text-sm font-medium text-gray-900 dark:text-white"
                id="id-support-menu-resources-label"
                data-testid="support-menu-resources-label"
              >
                {t('support-resources')}
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1" data-testid="support-menu-items">
            <DropdownMenuItem
              id="support-first-item"
              onClick={() => setAboutOpen(true)}
            >
              <span
                className="flex size-8 shrink-0 items-center justify-center"
                id="id-support-menu-about-icon"
                data-testid="support-menu-about-icon"
              >
                <Information size={20} />
              </span>
              {t('about')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/documentation')}>
              <span
                className="flex size-8 shrink-0 items-center justify-center"
                id="id-support-menu-docs-icon"
                data-testid="support-menu-docs-icon"
              >
                <img src={DocumentationIcon} alt="" width={20} height={20} />
              </span>
              {t('documentation-header')}
            </DropdownMenuItem>
            {showDiagnosticArchives && roles?.includes('ROLE_ADMIN') && (
              <DropdownMenuItem
                id="support-menu-diagnostic-archives"
                data-testid="support-menu-diagnostic-archives"
                onClick={() => navigate('/support')}
              >
                <span className="flex size-8 shrink-0 items-center justify-center">
                  <DocumentDownload size={18} />
                </span>
                {t('diagnostic-archives')}
              </DropdownMenuItem>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      {aboutOpen && <AboutCard onClose={() => setAboutOpen(false)} />}
    </>
  );
}

export default SupportMenu;
